import React, { useEffect } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlowProps,
  useReactFlow,
  Viewport,
} from 'reactflow';
import { Icon } from 'elements';
import messages from '../../../displayMessages';
import { intl } from 'i18n';
import useFullscreen from 'career-explorer/utils';
import { useLayoutSize } from 'utils';

interface BaseFlowMapProps {
  flowMapProps: ReactFlowProps;
  children?: React.ReactNode;
  backgroundColor?: string;
  showZoomControls?: boolean;
  fullscreenHeader?: React.ReactNode;
  isSidebarOpen?: boolean;
}

const BaseFlowMap: React.FC<BaseFlowMapProps> = ({
  flowMapProps,
  children,
  backgroundColor = '#F8FAFB',
  showZoomControls = false,
  fullscreenHeader,
  isSidebarOpen = false,
}) => {
  const { isFullscreen, loading, toggleFullscreen, elementRef } =
    useFullscreen();
  const isMobile = ['small', 'mobile'].includes(useLayoutSize());
  const [viewPort, setViewport] = React.useState<Viewport>();
  const reactFlow = useReactFlow();

  useEffect(() => {
    setViewport(undefined);
    if (!document.fullscreenEnabled) {
      const { x, y, zoom } = reactFlow.getViewport();
      if (isFullscreen) {
        reactFlow.setViewport(
          { x: x + 20, y: y + 150, zoom },
          { duration: 500 }
        );
      } else {
        reactFlow.setViewport(
          { x: x - 20, y: y - 150, zoom },
          { duration: 500 }
        );
      }
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    const offset = 120;
    const { x, y, zoom } = reactFlow.getViewport();
    const xPosition = viewPort?.x || x;
    const yPosition = viewPort?.y || y;

    if (!viewPort && x !== 0 && y !== 0) {
      setViewport({ x, y, zoom });
    }

    if (isSidebarOpen) {
      reactFlow.setViewport(
        { x: xPosition - offset, y: yPosition, zoom },
        { duration: 500 }
      );
    } else {
      reactFlow.setViewport(
        { x: x + offset, y: yPosition, zoom },
        { duration: 500 }
      );
    }
  }, [isSidebarOpen]);

  return (
    <div className='flow-map-container' ref={elementRef}>
      {isFullscreen && (
        <div className='full-screen-header' onClick={toggleFullscreen}>
          <Icon name='arrow-back' fill='#00AEEF' zoom='24px' />
          {fullscreenHeader}
        </div>
      )}
      {!loading && (
        <ReactFlow {...flowMapProps}>
          <Background
            color={backgroundColor}
            variant={BackgroundVariant.Lines}
            gap={0}
          />
          <button
            className='btn-full-screen'
            onClick={toggleFullscreen}
            title={intl.formatMessage(messages.fullScreenButtonTitle)}
          >
            <Icon name={isFullscreen ? 'full-screen-x' : 'full-screen'} />
          </button>
          {showZoomControls && (
            <Controls showInteractive={false} showFitView={false} />
          )}
          {children}
        </ReactFlow>
      )}
    </div>
  );
};

export default BaseFlowMap;
